if (!customElements.get('category-comparison')) {
    class CategoryComparison extends HTMLElement {
        constructor() {
            super();

            this.sectionId = this.dataset.sectionId;
            this.abortController = null;
        }

        connectedCallback() {
            this.elContent = this.querySelector('[data-category-comparison-content]');
            this.elCategories = this.querySelectorAll('.category-comparison--category');
            this.elStatus = this.querySelector('[data-category-comparison-status]');

            for (const elToggle of this.querySelectorAll('[data-category-handle]')) {
                elToggle.addEventListener('click', this.#handleCategoryClick.bind(this));
            }
        }

        #handleCategoryClick(event) {
            const elToggle = event.currentTarget;
            const elCategory = elToggle.closest('.category-comparison--category');

            if (elCategory?.classList.contains('active')) return;

            this.#fetchCategory(elToggle.dataset.categoryHandle, elToggle);
        }

        #buildSectionUrl(categoryHandle) {
            const sectionUrl = new URL(window.location.pathname, window.location.origin);
            sectionUrl.searchParams.set('section_id', this.sectionId);
            sectionUrl.searchParams.set('category', categoryHandle);
            return sectionUrl.toString();
        }

        #buildHistoryUrl(categoryHandle) {
            const url = new URL(window.location.href);
            url.searchParams.set('category', categoryHandle);
            url.searchParams.delete('section_id');
            return url.toString();
        }

        #wait(ms) {
            return new Promise((resolve) => {
                window.setTimeout(resolve, ms);
            });
        }

        #waitForImages(root, maxMs = 2500) {
            const images = [...root.querySelectorAll('img')];

            if (!images.length) return Promise.resolve();

            const imagesPromise = Promise.all(
                images.map((img) => {
                    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();

                    return new Promise((resolve) => {
                        img.addEventListener('load', resolve, { once: true });
                        img.addEventListener('error', resolve, { once: true });
                    });
                })
            );

            return Promise.race([imagesPromise, this.#wait(maxMs)]);
        }

        #waitBeforeFadeIn() {
            const holdDelay = Number(this.dataset.contentHoldDelay) || 400;

            return Promise.all([
                this.#waitForImages(this.elContent),
                this.#wait(holdDelay),
            ]);
        }

        #getTransitionDuration(el) {
            const duration = Number.parseFloat(getComputedStyle(el).transitionDuration);

            if (Number.isNaN(duration)) return 300;

            return duration * 1000;
        }

        #waitForOpacityTransition(el) {
            const duration = this.#getTransitionDuration(el);

            return new Promise((resolve) => {
                let settled = false;

                const finish = () => {
                    if (settled) return;

                    settled = true;
                    el.removeEventListener('transitionend', onTransitionEnd);
                    resolve();
                };

                const onTransitionEnd = (event) => {
                    if (event.target !== el || event.propertyName !== 'opacity') return;

                    finish();
                };

                el.addEventListener('transitionend', onTransitionEnd);
                window.setTimeout(finish, duration + 50);
            });
        }

        #fadeOut() {
            if (!this.elContent) return Promise.resolve();

            this.elContent.classList.add('is-faded');

            return this.#waitForOpacityTransition(this.elContent);
        }

        #fadeIn() {
            if (!this.elContent) return Promise.resolve();

            this.elContent.classList.remove('is-faded');

            return this.#waitForOpacityTransition(this.elContent);
        }

        #setActiveCategory(elToggle) {
            const elActiveCategory = elToggle.closest('.category-comparison--category');

            for (const elCategory of this.elCategories) {
                const isActive = elCategory === elActiveCategory;
                elCategory.classList.toggle('active', isActive);
                elCategory.querySelector('[data-category-handle]')?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            }
        }

        #announceCategory(elToggle) {
            if (!this.elStatus) return;

            const categoryName = elToggle.querySelector('span')?.textContent?.trim();
            const template = this.elStatus.dataset.statusTemplate;

            if (!categoryName || !template) return;

            this.elStatus.textContent = template.replace('__CATEGORY__', categoryName);
        }

        #fetchCategory(categoryHandle, elToggle) {
            this.abortController?.abort();
            this.abortController = new AbortController();
            const { signal } = this.abortController;

            this.elContent?.setAttribute('aria-busy', 'true');

            const fadeOutPromise = this.#fadeOut();
            const fetchPromise = fetch(this.#buildSectionUrl(categoryHandle), { signal })
                .then((response) => response.text());

            Promise.all([fadeOutPromise, fetchPromise])
                .then(([, responseText]) => {
                    if (signal.aborted) return;

                    const html = new DOMParser().parseFromString(responseText, 'text/html');
                    const elSection = html.querySelector(`#shopify-section-${this.sectionId}`);
                    const elNewContent = (elSection || html).querySelector('[data-category-comparison-content]');

                    if (!elNewContent || !this.elContent) return;

                    this.elContent.innerHTML = elNewContent.innerHTML;
                    window.initLazyLoad?.();
                    this.#setActiveCategory(elToggle);
                    this.#announceCategory(elToggle);
                    history.replaceState({}, '', this.#buildHistoryUrl(categoryHandle));

                    return this.#waitBeforeFadeIn().then(() => {
                        if (signal.aborted) return;

                        return this.#fadeIn();
                    });
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        this.elContent?.classList.remove('is-faded');
                        return;
                    }

                    console.error('category-comparison fetch failed', error);
                    this.elContent?.classList.remove('is-faded');
                })
                .finally(() => {
                    this.elContent?.removeAttribute('aria-busy');
                });
        }
    }

    customElements.define('category-comparison', CategoryComparison);
}
